import { NotificationBase } from "../types/notification.types";

const NOTIFICATIONS_MAX_COUNT = 50;

interface NotificationCacheEntry {
  messages: NotificationBase[];
  listeners: (() => void)[];
  maxCount: number;
}

interface NotificationCacheData {
  [storeId: string]: {
    [uid: string]: NotificationCacheEntry;
  };
}

// Custom cache used for notification messages instead of tanstack-query
// since the implementation will be specialized anyway.
// This cache keeps a sorted list on notifications truncated to maxCount.
class NotificationMessagesCache {
  private cachedMessages: NotificationCacheData = {};

  // Always return the same empty array instance since that is important to
  // react
  private emptyArray = [];

  setMaxCount(storeId: string, uid: string, maxCount: number) {
    const cacheEntry = this.getCacheEntryForStoreAndUid(storeId, uid);
    cacheEntry.maxCount = maxCount;
  }

  private getCacheEntryForStoreAndUid(
    storeId: string,
    uid: string
  ): NotificationCacheEntry {
    let storeCache = this.cachedMessages[storeId];
    if (!storeCache) {
      this.cachedMessages[storeId] = storeCache = {};
    }
    let uidCache = storeCache[uid];
    if (!uidCache) {
      storeCache[uid] = uidCache = {
        messages: this.emptyArray,
        listeners: [],
        maxCount: NOTIFICATIONS_MAX_COUNT,
      };
    }
    return uidCache;
  }

  private setMessagesForStoreAndUid(
    storeId: string,
    uid: string,
    messages: NotificationBase[]
  ) {
    const cacheEntry = this.getCacheEntryForStoreAndUid(storeId, uid);
    cacheEntry.messages = messages;
    for (const listener of cacheEntry.listeners) {
      listener();
    }
    return messages;
  }

  getMessagesForStoreAndUid(storeId: string, uid: string) {
    const { messages } = this.getCacheEntryForStoreAndUid(storeId, uid);
    return messages;
  }

  // Create a new array with "message" inserted into oldMessages.
  // Insert sort with newest message first.
  // Truncates array to maxCount.
  // Returns the new array
  private getUpdatedMessages(
    message: NotificationBase,
    oldMessages: NotificationBase[],
    cacheEntry: NotificationCacheEntry
  ) {
    const newMessages: NotificationBase[] = [];
    let addNotification = true;
    if (oldMessages) {
      for (const oldNotification of oldMessages) {
        if (oldNotification.id === message.id) {
          newMessages.push(message);
          addNotification = false;
        } else {
          newMessages.push(oldNotification);
        }
      }
    }

    // Insert in createdAt order
    if (addNotification) {
      for (let idx = 0; idx < newMessages.length; idx++) {
        if (
          new Date(message.createdAt).getTime() >
          new Date(newMessages[idx]!.createdAt).getTime()
        ) {
          newMessages.splice(idx, 0, message);
          addNotification = false;
          break;
        }
      }
    }

    // Add last
    if (addNotification) {
      newMessages.push(message);
    }

    return newMessages.slice(0, cacheEntry.maxCount);
  }

  // Loop through all "messages" and create new arrays.
  // When all messages has been inserted update the cache.
  update(storeId: string, uid: string, messages: NotificationBase[]) {
    const cacheEntry = this.getCacheEntryForStoreAndUid(storeId, uid);
    let newMessages = cacheEntry.messages;
    for (const message of messages) {
      newMessages = this.getUpdatedMessages(message, newMessages, cacheEntry);
    }
    this.setMessagesForStoreAndUid(storeId, uid, newMessages);
  }

  // Needed by react hook useSyncExternalStore
  subscribe(listener: () => void, storeId: string, uid: string) {
    const cacheEntry = this.getCacheEntryForStoreAndUid(storeId, uid);

    cacheEntry.listeners = [...cacheEntry.listeners, listener];
    return () => this.unsubscribe(listener, storeId, uid);
  }
  unsubscribe(listener: () => void, storeId: string, uid: string) {
    const cacheEntry = this.getCacheEntryForStoreAndUid(storeId, uid);
    cacheEntry.listeners = cacheEntry.listeners.filter((l) => l !== listener);
  }
}

export const notificationMessagesCache = new NotificationMessagesCache();
